import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';

import { subscriptionsService } from '@src/api/services';
import { IPreparePayData } from '@src/api/types/payment.types';
import { ISubscriptionsPlansRequest } from '@src/api/types/subscriptions.types';
import {
  GATEWAYS,
  IDateFormats,
  QUERY_KEYS,
  SEARCH_KEYS,
  TRIVIAL_DEFAULT_FIRST_PERIOD_DAYS,
} from '@src/constants';
import {
  IMarketingOfferCodes,
  MARKETING_OFFER_CODES,
} from '@src/constants/marketing.constants';
import { getToken } from '@src/helpers/axios.helpers';
import { getActiveUntilDate } from '@src/helpers/manage-subscription.helpers';
import { isMarketingOfferCode } from '@src/helpers/subscriptions.helpers';
import { IPlan } from '@src/interfaces/plans.interfaces';
import { ISubscriptionsOffersEligibility } from '@src/interfaces/subscriptions.interfaces';

interface IUseSubscriptionsPlans {
  preparePayData?: IPreparePayData;
  onSuccess?: (data?: IPlan[]) => Promise<void> | void;
  isWithDiscount?: boolean;
  offerCode?: IMarketingOfferCodes | (() => IMarketingOfferCodes | undefined);
}

export const useSubscriptionsPlans = ({
  preparePayData,
  onSuccess,
  isWithDiscount,
  offerCode,
}: IUseSubscriptionsPlans = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.subscriptionsPlans, preparePayData],
    queryFn: async () => {
      const data: ISubscriptionsPlansRequest = {
        currency: preparePayData?.currency,
        gateway: GATEWAYS.STRIPE,
        offer_code: typeof offerCode === 'function' ? offerCode() : offerCode,
      };

      const response = await subscriptionsService.plans(data);
      const plans = response.data.data || [];

      onSuccess?.(plans);
      return plans;
    },
    retry: false,
    retryOnMount: false,
    enabled: !!preparePayData,
    select: (data) => {
      if (isWithDiscount) return data;

      return data.filter(
        ({ first_period_days }) =>
          first_period_days > TRIVIAL_DEFAULT_FIRST_PERIOD_DAYS,
      );
    },
  });
};

interface IUseSubscriptionsMe {
  enabled?: boolean;
  format?: IDateFormats;
}

export const useSubscriptionsMe = ({
  enabled = true,
  format,
}: IUseSubscriptionsMe = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.subscriptionsMe],
    queryFn: async () => {
      const response = await subscriptionsService.me();
      return response.data;
    },
    select: (data) => {
      const { renewalDateValue, activeUntilDate, renewalDate } =
        getActiveUntilDate(data.subscription, format) || {};

      return {
        ...data,
        subscription: {
          ...data.subscription,
          renewalDateValue,
          activeUntilDate,
          renewalDate,
        },
      };
    },
    enabled: !!getToken() && enabled,
    retry: false,
    retryOnMount: false,
  });
};

interface IUseSubscriptionsOfferEligibility {
  offer_code?: IMarketingOfferCodes | null;
  enabled?: boolean;
}

export const useSubscriptionsOfferEligibility = ({
  enabled = true,
  offer_code,
}: IUseSubscriptionsOfferEligibility) => {
  return useQuery({
    queryKey: [QUERY_KEYS.subscriptionsOfferEligibility],
    queryFn: async () => {
      if (!offer_code) return;

      const response = await subscriptionsService.offerEligibility({
        offer_code,
      });

      return response.data.data;
    },
    enabled: enabled && !!offer_code,
  });
};

interface IUseSubscriptionsOffersEligibility {
  onSuccess?: (data: ISubscriptionsOffersEligibility) => void;
}

export const useSubscriptionsOffersEligibility = ({
  onSuccess,
}: IUseSubscriptionsOffersEligibility = {}) => {
  const OFFERS_ELIGIBILITY_REQUESTS = [
    () =>
      subscriptionsService.offerEligibility({
        offer_code: MARKETING_OFFER_CODES.RETURN,
      }),
    () =>
      subscriptionsService.offerEligibility({
        offer_code: MARKETING_OFFER_CODES.CANCELLATION,
      }),
    () =>
      subscriptionsService.offerEligibility({
        offer_code: MARKETING_OFFER_CODES.FREE_PERIOD_CANCEL,
      }),
    () => subscriptionsService.freePeriodEligibility(),
    () => subscriptionsService.pauseEligibility(),
  ];

  return useQuery({
    queryKey: [QUERY_KEYS.subscriptionsOffersEligibility],
    queryFn: async (): Promise<ISubscriptionsOffersEligibility> => {
      const results = await Promise.allSettled(
        OFFERS_ELIGIBILITY_REQUESTS.map((request) => request()),
      );

      // INFO: a failed request is treated as not eligible
      const isEligible = (result?: (typeof results)[number]) =>
        result?.status === 'fulfilled' && !!result.value.data.data?.eligible;

      const [
        returnOffer,
        cancellationOffer,
        freePeriodCancelOffer,
        freePeriod,
        pause,
      ] = results;

      const result = {
        isEligibleReturnOffer: isEligible(returnOffer),
        isEligibleCancellationOffer: isEligible(cancellationOffer),
        isEligibleFreePeriodCancelOffer: isEligible(freePeriodCancelOffer),
        isEligibleFreePeriod: isEligible(freePeriod),
        isEligiblePause: isEligible(pause),
        isNotEligibilityOffers: false,
      };

      onSuccess?.(result);

      return result;
    },
    select: (data) => ({
      ...data,
      isNotEligibilityOffers: !(
        data.isEligibleFreePeriod ||
        data.isEligibleReturnOffer ||
        data.isEligibleCancellationOffer ||
        data.isEligibleFreePeriodCancelOffer
      ),
    }),
    initialData: {
      isEligibleReturnOffer: false,
      isEligibleCancellationOffer: false,
      isEligibleFreePeriodCancelOffer: false,
      isEligibleFreePeriod: false,
      isEligiblePause: false,
      isNotEligibilityOffers: false,
    },
    placeholderData: undefined,
    enabled: true,
  });
};

interface IUseSubscriptionsOfferPreview {
  onError?: () => void;
}

export const useSubscriptionsOfferPreview = ({
  onError,
}: IUseSubscriptionsOfferPreview = {}) => {
  const [searchParams] = useSearchParams();

  const offer_code = searchParams.get(SEARCH_KEYS.OFFER_CODE);

  return useQuery({
    queryKey: [QUERY_KEYS.subscriptionsOfferPreview],
    queryFn: async () => {
      if (!offer_code || !isMarketingOfferCode(offer_code)) {
        throw Error(`User is not eligible for offer: ${offer_code}`);
      }

      const responseOfferEligibility =
        await subscriptionsService.offerEligibility({
          offer_code,
        });

      if (!responseOfferEligibility.data.data.eligible)
        throw Error(`User is not eligible for offer: ${offer_code}`);

      const response = await subscriptionsService.offerPreview({
        offer_code,
      });

      return {
        offer_code,
        eligible: responseOfferEligibility.data.data.eligible,
        ...response.data.data,
      };
    },
    select: (data) => {
      if (!data) return null;

      const { eligible, price = 1, base_price = 1 } = data;

      return {
        ...data,
        eligible,
        percentDiscount: Math.round(((base_price - price) / base_price) * 100),
      };
    },
    retry: false,
    retryOnMount: false,
    enabled: !!getToken(),
    placeholderData: undefined,
    throwOnError: (e) => {
      console.log(e);

      onError?.();
      return false;
    },
  });
};
